if (!customElements.get('yb-region-selector')) {
  const isCountryCode = (code) => /^[A-Z]{2}$/.test(code) && code !== 'XX';
  const getCountryFlag = (code) => isCountryCode(code)
    ? String.fromCodePoint(...[...code].map((letter) => letter.charCodeAt(0) + 127397)) : '🌐';
  const configuredLanguageCodes = Array.isArray(window.YB_AUTO_LOCALIZATION?.availableLanguages)
    ? window.YB_AUTO_LOCALIZATION.availableLanguages.map((languageCode) => String(languageCode)).filter(Boolean)
    : ['en'];
  const availableLanguageLookup = new Map(
    configuredLanguageCodes.map((languageCode) => [languageCode.toLowerCase(), languageCode])
  );

  const getAvailableLanguageCode = (preferredLanguage = 'en') => {
    const normalizedPreferredLanguage = String(preferredLanguage || 'en').toLowerCase();

    if (availableLanguageLookup.has(normalizedPreferredLanguage)) {
      return availableLanguageLookup.get(normalizedPreferredLanguage);
    }

    if (normalizedPreferredLanguage.split('-')[0] === 'zh') {
      const traditional = /(?:^|-)(?:hant|tw|hk|mo)(?:-|$)/.test(normalizedPreferredLanguage);
      const aliases = traditional ? ['zh-tw', 'zh-hant'] : ['zh-cn', 'zh-hans', 'zh'];
      for (const alias of aliases) {
        if (availableLanguageLookup.has(alias)) return availableLanguageLookup.get(alias);
      }
      return availableLanguageLookup.get('en') || 'en';
    }

    if (normalizedPreferredLanguage.includes('-')) {
      const baseLanguage = normalizedPreferredLanguage.split('-')[0];
      if (availableLanguageLookup.has(baseLanguage)) {
        return availableLanguageLookup.get(baseLanguage);
      }
    }

    return availableLanguageLookup.get('en') || 'en';
  };

  const parseJsonData = (value, fallback) => {
    try {
      return JSON.parse(String(value || ''));
    } catch (error) {
      return fallback;
    }
  };

  const guardError = (code) => Object.assign(new Error(code), { regionGuardCode: code });
  const canonicalize = (value) => {
    if (Array.isArray(value)) return value.map(canonicalize);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonicalize(value[key])]));
    }
    return value;
  };
  const cartFingerprint = (cart) => JSON.stringify(canonicalize({
    items: cart.items.map((item) => ({
      key: item.key,
      variant: item.variant_id,
      quantity: item.quantity,
      properties: item.properties || {},
      sellingPlan: item.selling_plan_allocation || null,
    })),
    attributes: cart.attributes || {},
    note: cart.note || '',
    discountCodes: cart.discount_codes || [],
    discounts: cart.cart_level_discount_applications || [],
  }));
  const cartIsAdding = () => Boolean(document.querySelector('product-form [type="submit"].loading, product-form [type="submit"][aria-busy="true"]'));
  const readJson = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) throw guardError('unverified');
      return await response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };
  const readCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await readJson(`${root.replace(/\/$/, '')}/cart.js`, { credentials: 'same-origin' });
    if (!Array.isArray(cart.items)) throw guardError('unverified');
    return cart;
  };
  const flushCart = async () => {
    if (cartIsAdding()) throw guardError('updating');
    if (document.querySelector('cart-items, cart-drawer-items') && !window.YBCartMutations?.flush) {
      throw guardError('updating');
    }
    if (window.YBCartMutations?.flush) await window.YBCartMutations.flush();
    if (cartIsAdding() || window.YBCartMutations?.isBusy?.()) throw guardError('updating');
  };
  const currentCookieCountry = () => {
    const entry = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith('localization='));
    if (!entry) return null;
    try {
      const country = decodeURIComponent(entry.slice('localization='.length)).toUpperCase();
      return isCountryCode(country) ? country : null;
    } catch (error) {
      return null;
    }
  };
  const assertCurrentCountry = (country) => {
    const cookieCountry = currentCookieCountry();
    if (cookieCountry && cookieCountry !== country) throw guardError('changed');
  };
  const validateDeliveryConfig = (config) => {
    const countriesValid = (countries) => Array.isArray(countries) && countries.length > 0 &&
      countries.every((country) => isCountryCode(country));
    if (config?.version !== 1 || !countriesValid(config.countries) || !Array.isArray(config.groups) ||
        !config.groups.every((group) => typeof group.id === 'string' && countriesValid(group.countries) &&
          Array.isArray(group.variants) && group.variants.every((id) => Number.isSafeInteger(id) && id > 0))) {
      throw guardError('unverified');
    }
    return config;
  };
  const validateCartDestination = async (cart, currentCountry, targetCountry, configUrl) => {
    if (!configUrl) throw guardError('unverified');
    const config = validateDeliveryConfig(await readJson(configUrl, { credentials: 'omit' }));
    if (!config.countries.includes(targetCountry)) throw guardError('destination');
    if (!cart.items.length) return;

    // The snapshot is generated from delivery profiles, not country/variant guesses.
    // A shared location group keeps the same fulfillable inventory pool when
    // changing destination. Unknown variants or changed groups fail closed.
    const quantities = new Map();
    for (const item of cart.items) {
      const variant = Number(item.variant_id);
      const groups = config.groups.filter((group) => group.variants.includes(variant));
      const sourcePools = groups.filter((group) => group.countries.includes(currentCountry)).map((group) => group.id).sort();
      const targetPools = groups.filter((group) => group.countries.includes(targetCountry)).map((group) => group.id).sort();
      if (!Number.isSafeInteger(variant) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
          item.selling_plan_allocation || !sourcePools.length || JSON.stringify(sourcePools) !== JSON.stringify(targetPools)) {
        throw guardError('unverified');
      }
      quantities.set(variant, (quantities.get(variant) || 0) + item.quantity);
    }
    if (quantities.size > 250) throw guardError('unverified');
    const ids = [...quantities.keys()].map((id) => `gid://shopify/ProductVariant/${id}`);
    const query = 'query RegionCartAvailability($country: CountryCode!, $ids: [ID!]!) @inContext(country: $country) { nodes(ids: $ids) { ... on ProductVariant { id availableForSale quantityRule { minimum maximum increment } } } }';
    const result = await readJson('/api/2026-10/graphql.json', {
      method: 'POST',
      credentials: 'omit',
      redirect: 'error',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables: { country: targetCountry, ids } }),
    });
    if (result.errors?.length || result.extensions?.context?.country !== targetCountry ||
        !Array.isArray(result.data?.nodes) || result.data.nodes.length !== ids.length) throw guardError('unverified');
    for (let index = 0; index < ids.length; index += 1) {
      const variant = result.data.nodes[index];
      if (variant?.id !== ids[index] || variant.availableForSale !== true) throw guardError('unavailable');
      const quantity = quantities.get(Number(variant.id.split('/').pop()));
      const rule = variant.quantityRule;
      if (!rule || !Number.isSafeInteger(rule.minimum) || !Number.isSafeInteger(rule.increment) || rule.increment < 1 ||
          (rule.maximum !== null && !Number.isSafeInteger(rule.maximum))) throw guardError('unverified');
      if (quantity < rule.minimum || quantity % rule.increment !== 0 ||
          (rule.maximum !== null && quantity > rule.maximum)) throw guardError('unavailable');
    }
  };
  window.YBRegionCartGuard = {
    async check(option, currentCountry, configUrl) {
      assertCurrentCountry(currentCountry);
      await flushCart();
      // A language-only save cannot change the cart's country or market.
      if (option.countryCode === currentCountry) return;
      const before = await readCart();
      await validateCartDestination(before, currentCountry, option.countryCode, configUrl);
      await flushCart();
      const after = await readCart();
      assertCurrentCountry(currentCountry);
      if (cartFingerprint(before) !== cartFingerprint(after)) throw guardError('changed');
      if (cartIsAdding() || window.YBCartMutations?.isBusy?.()) throw guardError('updating');
    },
  };
  let pendingRegionSelector = null;
  window.addEventListener('pageshow', (event) => {
    // A page restored after its localization POST must not retain a disabled
    // save button or the previous server market from the browser's back cache.
    if (event.persisted && pendingRegionSelector) window.location.reload();
  });

  const regionMessages = {
    en: {
      destination: 'Shipping is not currently available to this destination. Choose another destination or contact us. Your region and cart have not changed.',
      unavailable: 'This destination is not available for your current cart. Choose another destination. Your region and cart have not changed.',
      unverified: 'We could not verify this destination. Please try again. Your region and cart have not changed.',
      updating: 'Your cart is updating. Please try again in a moment. Your region has not changed.',
      changed: 'Your cart or region has just changed. Please reload this page and save your preference again.',
    },
    zh: {
      destination: '此地区暂未开通配送。请选择其他地区或联系客服确认；原地区和购物车保持不变。',
      unavailable: '当前购物车暂不支持配送至此地区。请选择其他地区；原地区和购物车保持不变。',
      unverified: '暂时无法确认此地区的配送情况，请稍后重试。原地区和购物车保持不变。',
      updating: '购物车正在更新，请稍后重试。配送地区尚未更改。',
      changed: '购物车或地区刚刚发生变化，请刷新页面后重新保存偏好。',
    },
    'zh-tw': {
      destination: '此地區暫未開通配送。請選擇其他地區或聯絡客服確認；原地區和購物車保持不變。',
      unavailable: '目前購物車暫不支援配送至此地區。請選擇其他地區；原地區和購物車保持不變。',
      unverified: '暫時無法確認此地區的配送情況，請稍後重試。原地區和購物車保持不變。',
      updating: '購物車正在更新，請稍後重試。配送地區尚未更改。',
      changed: '購物車或地區剛剛發生變化，請重新整理頁面後再次儲存偏好。',
    },
    de: {
      destination: 'Für dieses Ziel ist derzeit keine Lieferung verfügbar. Wählen Sie ein anderes Ziel oder kontaktieren Sie uns. Region und Warenkorb bleiben unverändert.',
      unavailable: 'Dieser Warenkorb kann nicht an dieses Ziel geliefert werden. Bitte wählen Sie ein anderes Ziel. Region und Warenkorb bleiben unverändert.',
      unverified: 'Das Lieferziel konnte nicht geprüft werden. Bitte versuchen Sie es erneut. Region und Warenkorb bleiben unverändert.',
      updating: 'Ihr Warenkorb wird aktualisiert. Bitte versuchen Sie es gleich erneut. Die Region bleibt unverändert.',
      changed: 'Warenkorb oder Region wurden gerade geändert. Bitte laden Sie die Seite neu und speichern Sie Ihre Auswahl erneut.',
    },
    fr: {
      destination: 'La livraison à cette destination n’est pas disponible actuellement. Choisissez une autre destination ou contactez-nous. Votre région et votre panier restent inchangés.',
      unavailable: 'Ce panier ne peut pas être livré à cette destination. Choisissez une autre destination. Votre région et votre panier restent inchangés.',
      unverified: 'Impossible de vérifier cette destination. Réessayez. Votre région et votre panier restent inchangés.',
      updating: 'Votre panier est en cours de mise à jour. Réessayez dans un instant. La région reste inchangée.',
      changed: 'Le panier ou la région vient de changer. Rechargez cette page et enregistrez à nouveau votre choix.',
    },
    it: {
      destination: 'La consegna a questa destinazione non è attualmente disponibile. Scegline un’altra o contattaci. Regione e carrello restano invariati.',
      unavailable: 'Questo carrello non può essere consegnato a questa destinazione. Scegline un’altra. Regione e carrello restano invariati.',
      unverified: 'Non è stato possibile verificare la destinazione. Riprova. Regione e carrello restano invariati.',
      updating: 'Il carrello si sta aggiornando. Riprova tra poco. La regione non è stata modificata.',
      changed: 'Il carrello o la regione sono appena cambiati. Ricarica la pagina e salva nuovamente la scelta.',
    },
    es: {
      destination: 'El envío a este destino no está disponible actualmente. Elige otro destino o contacta con nosotros. La región y el carrito no han cambiado.',
      unavailable: 'Este carrito no se puede enviar a ese destino. Elige otro destino. La región y el carrito no han cambiado.',
      unverified: 'No hemos podido verificar este destino. Inténtalo de nuevo. La región y el carrito no han cambiado.',
      updating: 'Tu carrito se está actualizando. Inténtalo de nuevo en un momento. La región no ha cambiado.',
      changed: 'El carrito o la región acaba de cambiar. Recarga la página y vuelve a guardar tu selección.',
    },
    pt: {
      destination: 'O envio para este destino não está disponível de momento. Escolha outro destino ou contacte-nos. A região e o carrinho não foram alterados.',
      unavailable: 'Este carrinho não pode ser enviado para este destino. Escolha outro. A região e o carrinho não foram alterados.',
      unverified: 'Não foi possível verificar este destino. Tente novamente. A região e o carrinho não foram alterados.',
      updating: 'O carrinho está a ser atualizado. Tente novamente dentro de instantes. A região não foi alterada.',
      changed: 'O carrinho ou a região acabou de mudar. Recarregue a página e guarde novamente a seleção.',
    },
  };

  customElements.define(
    'yb-region-selector',
    class YeahboxRegionSelector extends HTMLElement {
      constructor() {
        super();
        this.trigger = this.querySelector('.yb-region-selector__trigger');
        this.dialog = this.querySelector('.yb-region-selector__dialog');
        this.overlay = this.querySelector('.yb-region-selector__overlay');
        this.saveButton = this.querySelector('.yb-region-selector__save');
        this.errorMessage = this.querySelector('[data-region-error]');
        this.nativeForm = this.querySelector('.yb-region-selector__form');
        this.countryInput = this.querySelector('[data-region-country-input]');
        this.optionsContainer = this.querySelector('[data-region-options]');
        this.countryValue = this.querySelector('[data-region-country]');
        this.currencyValue = this.querySelector('[data-region-currency]');
        this.languageValue = this.querySelector('[data-region-language]');
        this.languageSelect = this.querySelector('[data-region-language-select]');
        this.summaryFull = this.querySelector('[data-region-summary-full]');
        this.summaryLocale = this.querySelector('[data-region-summary-locale]');
        this.summaryFlag = this.querySelector('[data-region-summary-flag]');
        this.activeTrigger = this.trigger;
        this.isSubmitting = false;
        this.hasRenderedOptions = false;
        this.defaultLanguageLabel = this.dataset.defaultLanguageLabel || '';
        const languageLabels = parseJsonData(this.dataset.regionLanguageLabels, {});
        const regionGroups = parseJsonData(this.dataset.regionGroups, []);
        const regionOptions = parseJsonData(this.dataset.regionOptions, []);
        this.languageLabels =
          languageLabels && typeof languageLabels === 'object' && !Array.isArray(languageLabels)
            ? languageLabels
            : {};
        this.supportedRegionGroups = (Array.isArray(regionGroups) ? regionGroups : []).filter(
          (group) => group && typeof group.id === 'string' && typeof group.label === 'string'
        );
        this.supportedOptions = (Array.isArray(regionOptions) ? regionOptions : [])
          .filter((option) => option && isCountryCode(String(option.countryCode || '').toUpperCase()))
          .map((option) => {
            const countryCode = String(option.countryCode).toUpperCase();
            const languageCode = this.dataset.currentLanguage || getAvailableLanguageCode();

            return {
              countryCode,
              countryName: String(option.countryName || countryCode),
              currencyCode: String(option.currencyCode || '').toUpperCase(),
              currencySymbol: String(option.currencySymbol || ''),
              flag: String(option.flag || getCountryFlag(countryCode)),
              regionGroup: String(option.regionGroup || ''),
              languageCode,
              languageLabel: this.getLanguageLabel(languageCode),
            };
          });
        this.currentCountry = String(this.dataset.currentCountry || '').toUpperCase();
        this.currentCountryName = this.dataset.currentCountryName || this.currentCountry;
        this.currentCurrency = String(this.dataset.currentCurrency || '').toUpperCase();
        this.currentLanguage = this.dataset.currentLanguage || '';
        this.currentLanguageLabel =
          this.dataset.currentLanguageLabel || this.currentLanguage || this.defaultLanguageLabel;
        this.defaultCountry = String(this.dataset.defaultCountry || this.currentCountry).toUpperCase();
        this.selectedOption = this.getInitialOption();
        this.onExternalTriggerClick = this.onExternalTriggerClick.bind(this);
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
      }

      connectedCallback() {
        this.trigger?.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle(event.currentTarget);
        });
        const saveTarget = this.nativeForm || this.saveButton;
        saveTarget?.addEventListener(this.nativeForm ? 'submit' : 'click', (event) => {
          event.preventDefault();
          this.submitLocalizationChange();
        });
        this.languageSelect?.addEventListener('change', (event) => {
          const languageCode = availableLanguageLookup.get(String(event.target.value).toLowerCase());
          if (!languageCode || !this.selectedOption) return;
          this.selectedOption = { ...this.selectedOption, languageCode, languageLabel: this.getLanguageLabel(languageCode) };
          this.updateSelectedState();
        });
        this.querySelectorAll('[data-region-close]').forEach((button) => {
          button.addEventListener('click', () => this.close());
        });
        document.addEventListener('click', this.onExternalTriggerClick);
        document.addEventListener('keydown', this.onDocumentKeyDown);
        this.updateSelectedState();
        this.updateSummary();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onExternalTriggerClick);
        document.removeEventListener('keydown', this.onDocumentKeyDown);
      }

      setStorageValue(key, value) {
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {
          // Manual form submission still works if storage is blocked.
        }
      }

      setCookieValue(key, value) {
        try {
          document.cookie = `${key}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax`;
        } catch (error) {
          // The native localization form remains usable when cookies are blocked.
        }
      }

      getLanguageLabel(languageCode) {
        const normalizedLanguage = String(languageCode || 'en').toLowerCase();
        const baseLanguage = normalizedLanguage.split('-')[0];

        return (
          this.languageLabels[normalizedLanguage] ||
          Array.from(this.languageSelect?.options || []).find((option) => option.value.toLowerCase() === normalizedLanguage)?.textContent ||
          this.languageLabels[baseLanguage] ||
          this.defaultLanguageLabel
        );
      }

      getOption(countryCode) {
        const normalized = String(countryCode || '').toUpperCase();
        const option = this.supportedOptions.find((item) => item.countryCode === normalized);
        if (!option) return null;
        const languageCode = this.selectedOption?.languageCode || this.currentLanguage || option.languageCode;
        return { ...option, languageCode, languageLabel: this.getLanguageLabel(languageCode) };
      }

      getFallbackOption() {
        return this.getOption(this.defaultCountry) || this.supportedOptions[0] || null;
      }

      getCurrentMarketOption() {
        const supportedOption = this.getOption(this.currentCountry);
        if (!this.currentCountry) return null;
        const languageCode = this.currentLanguage || getAvailableLanguageCode();

        return {
          countryCode: this.currentCountry,
          countryName: this.currentCountryName || this.currentCountry,
          currencyCode: this.currentCurrency,
          flag: supportedOption?.flag || '🌐',
          regionGroup: supportedOption?.regionGroup || '',
          languageCode,
          languageLabel: this.currentLanguageLabel || this.getLanguageLabel(languageCode),
        };
      }

      getInitialOption() {
        return this.getCurrentMarketOption() || this.getFallbackOption();
      }

      getCurrentMarketSummary() {
        const option = this.getCurrentMarketOption() || this.getFallbackOption();

        return option ? [option.countryName, option.languageLabel, option.currencyCode].filter(Boolean).join(' · ') : '';
      }

      getCurrentMarketLocaleSummary() {
        const option = this.getCurrentMarketOption() || this.getFallbackOption();

        return option ? [option.countryName, option.languageLabel].filter(Boolean).join(' / ') : '';
      }

      getCurrentMarketFlag() {
        const option = this.getCurrentMarketOption() || this.getFallbackOption();

        return option?.flag || '';
      }

      updateSummary() {
        if (this.summaryFull) this.summaryFull.textContent = this.getCurrentMarketSummary();
        if (this.summaryLocale) this.summaryLocale.textContent = this.getCurrentMarketLocaleSummary();
        if (this.summaryFlag) this.summaryFlag.textContent = this.getCurrentMarketFlag();
      }

      onExternalTriggerClick(event) {
        const trigger = event.target.closest('[data-yb-region-open]');
        if (!trigger) return;
        event.preventDefault();
        this.toggle(trigger);
      }

      onDocumentKeyDown(event) {
        if (event.key === 'Escape' && this.isOpen()) {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== 'Tab' || !this.isOpen()) return;
        const focusable = Array.from(this.dialog?.querySelectorAll?.('button:not([disabled]), select:not([disabled]), a[href]') || []);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      isOpen() {
        return Boolean(this.dialog && !this.dialog.hidden);
      }

      toggle(triggeredBy = this.trigger) {
        if (this.isOpen()) {
          this.close();
          return;
        }

        this.open(triggeredBy);
      }

      renderOptions() {
        if (!this.optionsContainer || this.hasRenderedOptions) return;

        const fragment = document.createDocumentFragment();
        this.supportedRegionGroups.forEach((group) => {
          const groupOptions = this.supportedOptions.filter((option) => option.regionGroup === group.id);
          if (!groupOptions.length) return;

          const section = document.createElement('section');
          section.className = 'yb-region-selector__option-group';
          section.setAttribute('aria-label', group.label);

          const heading = document.createElement('h4');
          heading.className = 'yb-region-selector__option-group-title';
          heading.textContent = group.label;
          section.appendChild(heading);

          groupOptions.forEach((option) => {
            const item = document.createElement('button');
            item.type = 'button';
            item.className = 'yb-region-selector__option';
            item.dataset.countryCode = option.countryCode;
            item.setAttribute(
              'aria-pressed',
              option.countryCode === this.selectedOption?.countryCode ? 'true' : 'false'
            );

            const main = document.createElement('span');
            main.className = 'yb-region-selector__option-main';
            const flag = document.createElement('span');
            flag.className = 'yb-region-selector__option-flag';
            flag.textContent = option.flag;
            const countryName = document.createElement('span');
            countryName.className = 'yb-region-selector__option-country';
            countryName.textContent = option.countryName;
            const languageName = document.createElement('span');
            languageName.className = 'yb-region-selector__option-language';
            languageName.textContent = [option.currencyCode, option.currencySymbol].filter(Boolean).join(' ');
            main.append(flag, countryName, languageName);

            item.append(main);

            item.addEventListener('click', () => {
              this.selectedOption = this.getOption(option.countryCode);
              this.updateSelectedState();
            });
            section.appendChild(item);
          });

          fragment.appendChild(section);
        });

        this.optionsContainer.appendChild(fragment);
        this.hasRenderedOptions = true;
      }

      updateSelectedState() {
        const option = this.selectedOption || this.getFallbackOption();
        if (!option) return;
        if (this.countryValue) this.countryValue.textContent = option.countryName;
        if (this.currencyValue) this.currencyValue.textContent = option.currencyCode;
        if (this.languageValue) this.languageValue.textContent = option.languageLabel;
        if (this.languageSelect) this.languageSelect.value = option.languageCode;
        if (this.countryInput) this.countryInput.value = option.countryCode;

        this.querySelectorAll('[data-region-options] .yb-region-selector__option').forEach((item) => {
          item.setAttribute('aria-pressed', item.dataset.countryCode === option.countryCode ? 'true' : 'false');
        });
      }

      setExpandedState(isExpanded) {
        this.trigger?.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        document.querySelectorAll('[data-yb-region-open]').forEach((button) => {
          button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        });
      }

      open(triggeredBy = this.trigger) {
        this.clearError();
        this.activeTrigger = triggeredBy || this.trigger;
        this.selectedOption = this.getInitialOption();
        this.renderOptions();
        this.updateSelectedState();
        this.dialog?.removeAttribute('hidden');
        this.dialog?.removeAttribute('aria-hidden');
        this.dialog?.removeAttribute('inert');
        this.overlay?.removeAttribute('hidden');
        this.overlay?.removeAttribute('aria-hidden');
        this.overlay?.removeAttribute('inert');
        this.setExpandedState(true);
        document.documentElement.classList.add('yb-region-selector-open');
        const focusTarget = this.querySelector('.yb-region-selector__option[aria-pressed="true"]') ||
          this.querySelector('.yb-region-selector__close');
        focusTarget?.focus();
      }

      close() {
        this.dialog?.setAttribute('hidden', '');
        this.dialog?.setAttribute('aria-hidden', 'true');
        this.dialog?.setAttribute('inert', '');
        this.overlay?.setAttribute('hidden', '');
        this.overlay?.setAttribute('aria-hidden', 'true');
        this.overlay?.setAttribute('inert', '');
        this.setExpandedState(false);
        document.documentElement.classList.remove('yb-region-selector-open');
        this.activeTrigger?.focus();
      }

      setManualPreference(option) {
        this.setStorageValue('yb-localization-manual-choice', 'true');
        this.setStorageValue('yb-localization-country', option.countryCode);
        this.setStorageValue('yb-localization-language', option.languageCode);
        this.setCookieValue('yb-localization-manual-choice', 'true');
        this.setCookieValue('yb-localization-country', option.countryCode);
        this.setCookieValue('yb-localization-language', option.languageCode);
      }

      clearError() {
        if (!this.errorMessage) return;
        this.errorMessage.textContent = '';
        this.errorMessage.hidden = true;
      }

      showError(code) {
        if (!this.errorMessage) return;
        const language = String(this.currentLanguage || 'en').toLowerCase();
        const key = /^(zh-tw|zh-hant)/.test(language) ? 'zh-tw' : language.split('-')[0];
        const messages = regionMessages[key] || regionMessages.en;
        this.errorMessage.textContent = messages[code] || messages.unverified;
        this.errorMessage.hidden = false;
        this.errorMessage.scrollIntoView?.({ block: 'nearest' });
      }

      async submitLocalizationChange() {
        if (this.isSubmitting) return;
        const selected = this.selectedOption || this.getFallbackOption();
        if (!selected) return;
        if (pendingRegionSelector) {
          this.showError('updating');
          return;
        }
        const option = { ...selected };

        this.isSubmitting = true;
        pendingRegionSelector = this;
        this.clearError();
        if (this.saveButton) {
          this.saveButton.disabled = true;
          this.saveButton.setAttribute('aria-busy', 'true');
        }
        let submitted = false;
        try {
          await window.YBRegionCartGuard.check(option, this.currentCountry, this.dataset.deliveryEligibilityUrl);
          if (this.selectedOption?.countryCode !== option.countryCode ||
              this.selectedOption?.languageCode !== option.languageCode || (this.dialog && !this.isOpen())) {
            throw guardError('changed');
          }

          const form = this.nativeForm || document.createElement('form');
          if (!this.nativeForm) {
            form.method = 'POST';
            form.action = this.dataset.localizationPath || '/localization';
            form.acceptCharset = 'UTF-8';
            form.style.display = 'none';
          }

          this.appendHiddenField(form, 'form_type', 'localization');
          this.appendHiddenField(form, 'utf8', '✓');
          this.appendHiddenField(form, '_method', 'put');
          this.appendHiddenField(form, 'country_code', option.countryCode);
          this.appendHiddenField(form, 'language_code', option.languageCode);
          this.appendHiddenField(form, 'return_to', this.getReturnPath(option));

          if (!this.nativeForm) document.body.appendChild(form);
          // A rejected preflight or a native submission error never commits a preference.
          form.submit();
          this.setManualPreference(option);
          submitted = true;
        } catch (error) {
          this.showError(error.regionGuardCode || 'unverified');
          this.isSubmitting = false;
          if (this.saveButton) {
            this.saveButton.disabled = false;
            this.saveButton.removeAttribute('aria-busy');
          }
        } finally {
          if (!submitted) pendingRegionSelector = null;
        }
      }

      getReturnPath(option) {
        let pathname = window.location.pathname;
        const changed = option.countryCode !== this.currentCountry || option.languageCode.toLowerCase() !== this.currentLanguage.toLowerCase();
        if (changed) {
          const firstSegment = pathname.split('/')[1]?.toLowerCase() || '';
          const rootSegment = window.Shopify?.routes?.root?.split('/')[1]?.toLowerCase();
          const routeCodes = new Set(availableLanguageLookup.keys());
          if (['zh', 'zh-cn', 'zh-hans'].some((code) => availableLanguageLookup.has(code))) {
            ['zh', 'zh-cn', 'zh-hans'].forEach((code) => routeCodes.add(code));
          }
          if (['zh-tw', 'zh-hant'].some((code) => availableLanguageLookup.has(code))) {
            ['zh-tw', 'zh-hant'].forEach((code) => routeCodes.add(code));
          }
          const knownLanguage = [...routeCodes].some((code) => {
            const base = code.split('-')[0];
            const suffix = firstSegment.slice(code.length + 1);
            return firstSegment === code || (base !== 'zh' && firstSegment === base) ||
              (firstSegment.startsWith(`${code}-`) && isCountryCode(suffix.toUpperCase()));
          });
          if (knownLanguage || (rootSegment && firstSegment === rootSegment)) {
            const nextSlash = pathname.indexOf('/', 1);
            pathname = nextSlash < 0 ? '/' : pathname.slice(nextSlash);
          }
        }
        const query = String(window.location.search || '').replace(/^\?/, '').split('&').filter((part) => {
          const key = part.split('=', 1)[0];
          try {
            return !['country', 'currency'].includes(decodeURIComponent(key.replace(/\+/g, ' ')).toLowerCase());
          } catch (error) {
            return true;
          }
        }).join('&');
        return `${pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
      }

      appendHiddenField(form, name, value) {
        const existingInput = form.querySelector?.(`[name="${name}"]`);
        const input = existingInput || document.createElement('input');
        if (!existingInput) input.type = 'hidden';
        input.name = name;
        input.value = value;
        if (!existingInput) form.appendChild(input);
      }
    }
  );
}
